/**
 * Tool Hub — admin pagina voor het beheren van publieke (embedbare) tools.
 * Eerste tool: Meeting Timer. De pagina is opgezet met tabs zodat
 * toekomstige tools eenvoudig kunnen worden toegevoegd.
 */
import { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Wrench, Timer, Lightbulb, ListChecks, Settings as SettingsIcon,
  Plus, Trash2, ChevronUp, ChevronDown, Save, Loader2, Copy, Check,
  Volume2, RotateCcw, ExternalLink, AlertCircle, ClipboardList, Mail,
  BookOpen, Upload, FileText, Image as ImageIcon, Library as LibraryIcon,
  Edit3, Eye, Download as DownloadIcon, Tag, Users, Phone, Building2,
  Activity, Heart, Euro, TrendingUp, Sparkles, Award, Coffee, Wind, Droplets, Brain, Zap, MoveRight
} from 'lucide-react';
import { toast } from 'sonner';
import { meetingTimerAPI, ebookLibraryAPI, vitaliteitBenchmarkAPI, arbeidsvoorwaardenCheckAPI, pomodoroTimerAPI } from '@/lib/api';
import { previewSound } from '@/lib/timerAudio';
import BookingCtaSettings from '@/components/BookingCtaSettings';

// Helper: voeg de 4 booking-cta velden toe aan een save-payload.
const withBookingFields = (payload, config) => ({
  ...payload,
  booking_cta_enabled: config?.booking_cta_enabled !== false,
  booking_cta_label: config?.booking_cta_label ?? '',
  booking_cta_subtitle: config?.booking_cta_subtitle ?? '',
  booking_url: config?.booking_url ?? '',
});

const QUESTION_TYPES = [
  { value: 'text', label: 'Tekst (kort)' },
  { value: 'textarea', label: 'Tekst (lang)' },
  { value: 'number', label: 'Nummer' },
  { value: 'duration', label: 'Duur (minuten)' },
  { value: 'single_choice', label: 'Single choice' },
  { value: 'multi_choice', label: 'Multi choice' },
];

const SOUND_OPTIONS = [
  { value: 'bell', label: 'Klassieke bel' },
  { value: 'gong', label: 'Gong' },
  { value: 'alarm', label: 'Alarm beeps' },
];

export default function ToolHubPage() {
  return (
    <div className="space-y-5 fade-in" data-testid="tool-hub-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Wrench className="w-6 h-6 text-orange-600" /> Tool Hub
        </h1>
        <p className="text-muted-foreground text-sm">
          Beheer hier de publieke, embedbare tools. Iedere tool maakt automatisch een lead aan in het CRM zodra een bezoeker zijn gegevens invult.
        </p>
      </div>

      <Tabs defaultValue="meeting-timer">
        <TabsList className="w-full justify-start flex-wrap h-auto bg-muted/50 p-1">
          <TabsTrigger value="meeting-timer" data-testid="tool-tab-meeting-timer" className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Timer className="w-4 h-4" /> Meeting Timer
          </TabsTrigger>
          <TabsTrigger value="ebook-library" data-testid="tool-tab-ebook-library" className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <LibraryIcon className="w-4 h-4" /> Ebook Bibliotheek
          </TabsTrigger>
          <TabsTrigger value="vitaliteit-benchmark" data-testid="tool-tab-vitaliteit-benchmark" className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Activity className="w-4 h-4" /> Vitaliteit Benchmark
          </TabsTrigger>
          <TabsTrigger value="arbeidsvoorwaarden-check" data-testid="tool-tab-arbeidsvoorwaarden-check" className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Award className="w-4 h-4" /> Arbeidsvoorwaarden Check
          </TabsTrigger>
          <TabsTrigger value="pomodoro-timer" data-testid="tool-tab-pomodoro-timer" className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Coffee className="w-4 h-4" /> Pomodoro Timer
          </TabsTrigger>
        </TabsList>

        <TabsContent value="meeting-timer" className="mt-5">
          <MeetingTimerManager />
        </TabsContent>

        <TabsContent value="ebook-library" className="mt-5">
          <EbookLibraryManager />
        </TabsContent>

        <TabsContent value="vitaliteit-benchmark" className="mt-5">
          <VitaliteitBenchmarkManager />
        </TabsContent>

        <TabsContent value="arbeidsvoorwaarden-check" className="mt-5">
          <ArbeidsvoorwaardenCheckManager />
        </TabsContent>

        <TabsContent value="pomodoro-timer" className="mt-5">
          <PomodoroTimerManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MeetingTimerManager() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  const load = async () => {
    try {
      const res = await meetingTimerAPI.adminGetConfig();
      setConfig(normalizeConfig(res.data));
    } catch (e) {
      toast.error('Kon configuratie niet laden.');
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await meetingTimerAPI.adminListSessions(50);
      setSessions(res.data?.sessions || []);
    } catch (e) {
      // niet kritiek
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => { load(); loadSessions(); }, []);

  const handleSave = async () => {
    if (!config) return;
    // Valideer dat elke vraag een unieke, niet-lege key heeft
    const keys = new Set();
    for (const q of config.questions || []) {
      const k = (q.key || '').trim();
      if (!k) {
        toast.error(`Vraag "${q.label || '(geen titel)'}" mist een sleutel (key).`);
        return;
      }
      if (keys.has(k)) {
        toast.error(`Dubbele vraag-sleutel: "${k}".`);
        return;
      }
      keys.add(k);
      if (!(q.label || '').trim()) {
        toast.error(`Vraag met sleutel "${k}" heeft geen titel.`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = withBookingFields({
        intro_title: config.intro_title,
        intro_text: config.intro_text,
        default_duration_min: Number(config.default_duration_min) || 30,
        sound_key: config.sound_key || 'bell',
        questions: (config.questions || []).map(stripTransient),
        tips: (config.tips || []).map(({ _expanded, ...rest }) => rest),
        active: config.active !== false,
      }, config);
      const res = await meetingTimerAPI.adminUpdateConfig(payload);
      setConfig(normalizeConfig(res.data));
      toast.success('Configuratie opgeslagen.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('Weet je zeker dat je terug wilt naar de standaard vragen en tips? Eigen aanpassingen gaan verloren.')) return;
    setSaving(true);
    try {
      const res = await meetingTimerAPI.adminResetDefaults();
      setConfig(normalizeConfig(res.data));
      toast.success('Standaard-config hersteld.');
    } catch (e) {
      toast.error('Reset mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const embedUrl = `${window.location.origin}/meeting-timer`;
  const embedSnippet = `<iframe src="${embedUrl}" width="100%" height="900" frameborder="0" style="border:0; max-width:900px;"></iframe>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopiedEmbed(true);
      toast.success('Embed-code gekopieerd.');
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch (e) {
      toast.error('Kopiëren mislukt.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  if (!config) {
    return <div className="text-sm text-muted-foreground">Geen configuratie beschikbaar.</div>;
  }

  return (
    <div className="space-y-5" data-testid="meeting-timer-manager">
      {/* Embed / preview */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-orange-600" /> Embed & preview
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Deel deze link of plak de embed-code in je site / WordPress / Notion.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <Input
                  readOnly
                  value={embedUrl}
                  className="bg-muted/40 text-xs"
                  data-testid="embed-url-input"
                />
                <a
                  href={embedUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-2 text-xs font-medium rounded-md bg-orange-600 text-white hover:bg-orange-700 whitespace-nowrap"
                >
                  Open in nieuw tabblad
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={embedSnippet}
                  className="bg-muted/40 text-xs font-mono"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyEmbed}
                  className="whitespace-nowrap"
                  data-testid="copy-embed-btn"
                >
                  {copiedEmbed ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copiedEmbed ? 'Gekopieerd' : 'Kopieer'}
                </Button>
              </div>
            </div>
            <div className="flex flex-col items-start gap-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
              <div className="flex items-center gap-2">
                <Switch
                  checked={config.active !== false}
                  onCheckedChange={(v) => setConfig(c => ({ ...c, active: v }))}
                  data-testid="active-switch"
                />
                <span className="text-sm">{config.active !== false ? 'Actief' : 'Gepauzeerd'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* General settings */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-base flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-orange-600" /> Algemene instellingen
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Intro titel</Label>
              <Input
                value={config.intro_title || ''}
                onChange={e => setConfig(c => ({ ...c, intro_title: e.target.value }))}
                data-testid="intro-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Default timer-duur (minuten)</Label>
              <Input
                type="number"
                min="1"
                max="480"
                value={config.default_duration_min ?? 30}
                onChange={e => setConfig(c => ({ ...c, default_duration_min: Number(e.target.value) }))}
                data-testid="default-duration-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Intro tekst</Label>
            <Textarea
              value={config.intro_text || ''}
              onChange={e => setConfig(c => ({ ...c, intro_text: e.target.value }))}
              className="min-h-[80px]"
              data-testid="intro-text-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Eindgeluid</Label>
            <div className="flex flex-wrap items-center gap-2">
              {SOUND_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setConfig(c => ({ ...c, sound_key: opt.value }))}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                    config.sound_key === opt.value
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-orange-300'
                  }`}
                  data-testid={`sound-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => previewSound(config.sound_key || 'bell')}
                data-testid="preview-sound-btn"
              >
                <Volume2 className="w-4 h-4 mr-1" /> Test geluid
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questions */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-orange-600" /> Meeting-vragen
              <Badge variant="secondary" className="ml-1">{(config.questions || []).length}</Badge>
            </h3>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfig(c => ({
                ...c,
                questions: [
                  ...(c.questions || []),
                  { key: `q${Date.now()}`, label: 'Nieuwe vraag', type: 'text', required: false, options: [], _expanded: true },
                ],
              }))}
              data-testid="add-question-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Voeg vraag toe
            </Button>
          </div>

          <div className="space-y-3">
            {(config.questions || []).map((q, idx) => (
              <QuestionEditor
                key={idx}
                q={q}
                idx={idx}
                total={(config.questions || []).length}
                onChange={(patch) => setConfig(c => ({
                  ...c,
                  questions: c.questions.map((qq, i) => i === idx ? { ...qq, ...patch } : qq),
                }))}
                onMove={(dir) => setConfig(c => {
                  const arr = [...c.questions];
                  const ni = idx + dir;
                  if (ni < 0 || ni >= arr.length) return c;
                  [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
                  return { ...c, questions: arr };
                })}
                onRemove={() => {
                  if (!window.confirm(`Vraag "${q.label}" verwijderen?`)) return;
                  setConfig(c => ({ ...c, questions: c.questions.filter((_, i) => i !== idx) }));
                }}
              />
            ))}
            {(config.questions || []).length === 0 && (
              <p className="text-sm text-muted-foreground italic">Nog geen vragen — voeg er minimaal één toe.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-600" /> Vergader-tips
              <Badge variant="secondary" className="ml-1">{(config.tips || []).length}</Badge>
            </h3>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfig(c => ({
                ...c,
                tips: [...(c.tips || []), { text: '', category: '' }],
              }))}
              data-testid="add-tip-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Voeg tip toe
            </Button>
          </div>

          <div className="space-y-2">
            {(config.tips || []).map((tip, idx) => (
              <div key={idx} className="flex items-start gap-2 p-3 border border-slate-100 dark:border-slate-800 rounded-md bg-amber-50/40 dark:bg-amber-950/10">
                <Lightbulb className="w-4 h-4 text-amber-600 flex-shrink-0 mt-2" />
                <div className="flex-1 space-y-2">
                  <Textarea
                    value={tip.text || ''}
                    onChange={e => setConfig(c => ({
                      ...c,
                      tips: c.tips.map((t, i) => i === idx ? { ...t, text: e.target.value } : t),
                    }))}
                    placeholder="Schrijf hier een korte, krachtige vergader-tip…"
                    className="min-h-[60px] bg-white"
                  />
                  <Input
                    value={tip.category || ''}
                    onChange={e => setConfig(c => ({
                      ...c,
                      tips: c.tips.map((t, i) => i === idx ? { ...t, category: e.target.value } : t),
                    }))}
                    placeholder="Categorie (optioneel, bijv. focus / start / einde)"
                    className="bg-white text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm('Tip verwijderen?')) return;
                    setConfig(c => ({ ...c, tips: c.tips.filter((_, i) => i !== idx) }));
                  }}
                  className="text-slate-400 hover:text-red-600 p-1"
                  title="Verwijderen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {(config.tips || []).length === 0 && (
              <p className="text-sm text-muted-foreground italic">Nog geen tips toegevoegd.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sessions */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-slate-500" /> Recente sessies
              <Badge variant="secondary" className="ml-1">{sessions.length}</Badge>
            </h3>
            <Button size="sm" variant="ghost" onClick={loadSessions} disabled={sessionsLoading}>
              {sessionsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vernieuwen'}
            </Button>
          </div>
          {sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nog geen sessies gestart.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {sessions.map((s) => (
                <div key={s.id} className="flex items-start justify-between gap-3 p-3 border border-slate-100 dark:border-slate-800 rounded-md">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{s.contact?.name || '–'}</span>
                      <a href={`mailto:${s.contact?.email}`} className="text-xs text-slate-500 hover:text-orange-700 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {s.contact?.email}
                      </a>
                      {s.contact?.company && <span className="text-xs text-slate-400">· {s.contact.company}</span>}
                    </div>
                    {s.meeting?.subject && (
                      <p className="text-xs text-slate-600 mt-1 truncate">📌 {s.meeting.subject}</p>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1">
                      Duur: {s.duration_min} min · {new Date(s.created_at).toLocaleString('nl-NL')}
                    </p>
                  </div>
                  {s.lead_id && (
                    <a
                      href={`/leads/${s.lead_id}`}
                      className="text-xs text-orange-700 hover:underline whitespace-nowrap"
                    >
                      Naar lead →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking CTA */}
      <BookingCtaSettings
        cfg={config}
        onChange={(field, value) => setConfig((c) => ({ ...c, [field]: value }))}
      />

      {/* Actions */}
      <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-border py-3 -mx-1 px-1 flex items-center justify-between flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={handleResetDefaults}
          disabled={saving}
          data-testid="reset-defaults-btn"
        >
          <RotateCcw className="w-4 h-4 mr-1" /> Standaard herstellen
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="save-config-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Opslaan
        </Button>
      </div>
    </div>
  );
}

function QuestionEditor({ q, idx, total, onChange, onMove, onRemove }) {
  const expanded = q._expanded !== false; // default expanded
  const setExpanded = (v) => onChange({ _expanded: v });

  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-md bg-card">
      <div className="flex items-center gap-2 p-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex flex-col gap-0.5">
          <button
            disabled={idx === 0}
            onClick={() => onMove(-1)}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 p-0.5"
            title="Naar boven"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            disabled={idx === total - 1}
            onClick={() => onMove(1)}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 p-0.5"
            title="Naar beneden"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 text-left text-sm font-medium hover:text-orange-700"
        >
          {q.label || '(geen titel)'} <span className="text-[10px] text-slate-400 uppercase ml-2">{q.type}</span>
          {q.required && <Badge variant="outline" className="ml-2 text-[9px] py-0 px-1.5">Verplicht</Badge>}
        </button>
        <button
          onClick={onRemove}
          className="text-slate-400 hover:text-red-600 p-1"
          title="Verwijderen"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="p-3 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Sleutel (key)</Label>
              <Input
                value={q.key || ''}
                onChange={e => onChange({ key: e.target.value.replace(/\s+/g, '_').toLowerCase() })}
                placeholder="bijv. subject"
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <select
                value={q.type || 'text'}
                onChange={e => onChange({ type: e.target.value })}
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
              >
                {QUESTION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Titel (label)</Label>
            <Input
              value={q.label || ''}
              onChange={e => onChange({ label: e.target.value })}
              className="text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Beschrijving (optioneel)</Label>
            <Input
              value={q.description || ''}
              onChange={e => onChange({ description: e.target.value })}
              className="text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Placeholder (optioneel)</Label>
            <Input
              value={q.placeholder || ''}
              onChange={e => onChange({ placeholder: e.target.value })}
              className="text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={!!q.required}
              onCheckedChange={(v) => onChange({ required: v })}
            />
            <Label className="text-sm">Verplicht veld</Label>
          </div>

          {(q.type === 'single_choice' || q.type === 'multi_choice') && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Opties</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange({ options: [...(q.options || []), { label: '', value: '' }] })}
                >
                  <Plus className="w-3 h-3 mr-1" /> Optie
                </Button>
              </div>
              {(q.options || []).map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <Input
                    placeholder="Label"
                    value={opt.label || ''}
                    onChange={e => onChange({
                      options: q.options.map((o, i) => i === oi ? { ...o, label: e.target.value } : o),
                    })}
                    className="text-sm"
                  />
                  <Input
                    placeholder="Waarde (kort)"
                    value={opt.value || ''}
                    onChange={e => onChange({
                      options: q.options.map((o, i) => i === oi ? { ...o, value: e.target.value } : o),
                    })}
                    className="text-sm"
                  />
                  <button
                    onClick={() => onChange({ options: q.options.filter((_, i) => i !== oi) })}
                    className="text-slate-400 hover:text-red-600 p-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───
function normalizeConfig(cfg) {
  return {
    ...cfg,
    questions: (cfg?.questions || []).map(q => ({ ...q, _expanded: false })),
    tips: (cfg?.tips || []).map(t => ({ ...t })),
  };
}

function stripTransient(q) {
  const { _expanded, ...rest } = q;
  return rest;
}


// ═══════════════════════════════════════════════════════════════
// EBOOK LIBRARY MANAGER
// ═══════════════════════════════════════════════════════════════

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatEUR(price) {
  const n = Number(price || 0);
  if (n === 0) return 'Gratis';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);
}

function EbookLibraryManager() {
  const [ebooks, setEbooks] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [bookingCfg, setBookingCfg] = useState(null);
  const [savingBooking, setSavingBooking] = useState(false);

  const load = async () => {
    try {
      const res = await ebookLibraryAPI.adminList();
      setEbooks(res.data?.ebooks || []);
    } catch (e) {
      toast.error('Kon ebooks niet laden.');
    } finally {
      setLoading(false);
    }
  };

  const loadBookingCfg = async () => {
    try {
      const res = await ebookLibraryAPI.adminGetConfig();
      setBookingCfg(res.data);
    } catch (e) { /* niet kritiek */ }
  };

  const handleSaveBookingCfg = async () => {
    if (!bookingCfg) return;
    setSavingBooking(true);
    try {
      const res = await ebookLibraryAPI.adminUpdateConfig({
        booking_cta_enabled: bookingCfg.booking_cta_enabled !== false,
        booking_cta_label: bookingCfg.booking_cta_label || '',
        booking_cta_subtitle: bookingCfg.booking_cta_subtitle || '',
        booking_url: bookingCfg.booking_url || '',
      });
      setBookingCfg(res.data);
      toast.success('Booking CTA opgeslagen.');
    } catch (e) {
      toast.error('Opslaan mislukt.');
    } finally {
      setSavingBooking(false);
    }
  };

  const loadGrants = async () => {
    setGrantsLoading(true);
    try {
      const res = await ebookLibraryAPI.adminListGrants(50);
      setGrants(res.data?.grants || []);
    } catch (e) {
      // niet kritiek
    } finally {
      setGrantsLoading(false);
    }
  };

  useEffect(() => { load(); loadGrants(); loadBookingCfg(); }, []);

  const embedUrl = `${window.location.origin}/ebook-library`;
  const embedSnippet = `<iframe src="${embedUrl}" width="100%" height="900" frameborder="0" style="border:0;"></iframe>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopiedEmbed(true);
      toast.success('Embed-code gekopieerd.');
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch (e) {
      toast.error('Kopiëren mislukt.');
    }
  };

  const handleDelete = async (ebook) => {
    if (!window.confirm(`Weet je zeker dat je "${ebook.title}" wilt verwijderen? Dit verwijdert ook het PDF-bestand en de cover.`)) return;
    try {
      await ebookLibraryAPI.adminDelete(ebook.id);
      toast.success('Ebook verwijderd.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verwijderen mislukt.');
    }
  };

  const handleToggleActive = async (ebook) => {
    try {
      await ebookLibraryAPI.adminUpdate(ebook.id, { active: !ebook.active });
      toast.success(ebook.active ? 'Ebook gedeactiveerd.' : 'Ebook actief.');
      await load();
    } catch (e) {
      toast.error('Status updaten mislukt.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ebook-library-manager">
      {/* Embed / preview */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-orange-600" /> Embed & preview
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Deel deze link of plak de embed-code in je site / WordPress / Notion. Bezoekers vullen eenmalig
                hun naam + email in en krijgen direct toegang tot alle actieve ebooks.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <Input
                  readOnly
                  value={embedUrl}
                  className="bg-muted/40 text-xs"
                  data-testid="ebook-embed-url"
                />
                <a
                  href={embedUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-2 text-xs font-medium rounded-md bg-orange-600 text-white hover:bg-orange-700 whitespace-nowrap"
                >
                  Open in nieuw tabblad
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={embedSnippet}
                  className="bg-muted/40 text-xs font-mono"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyEmbed}
                  className="whitespace-nowrap"
                  data-testid="ebook-copy-embed-btn"
                >
                  {copiedEmbed ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copiedEmbed ? 'Gekopieerd' : 'Kopieer'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ebooks list + add button */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-orange-600" /> Ebooks
              <Badge variant="secondary" className="ml-1">{ebooks.length}</Badge>
            </h3>
            <Button
              size="sm"
              onClick={() => { setEditingId(null); setShowForm(true); }}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="add-ebook-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Nieuwe ebook
            </Button>
          </div>

          {ebooks.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-4 text-center">
              Nog geen ebooks geüpload. Klik op "Nieuwe ebook" om er één toe te voegen.
            </p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {ebooks.map(book => (
                <EbookRow
                  key={book.id}
                  book={book}
                  onEdit={() => { setEditingId(book.id); setShowForm(true); }}
                  onDelete={() => handleDelete(book)}
                  onToggleActive={() => handleToggleActive(book)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Grants / leads log */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" /> Recente toegangsaanvragen
              <Badge variant="secondary" className="ml-1">{grants.length}</Badge>
            </h3>
            <Button size="sm" variant="ghost" onClick={loadGrants} disabled={grantsLoading}>
              {grantsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vernieuwen'}
            </Button>
          </div>
          {grants.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nog geen aanvragen.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {grants.map((g) => (
                <div key={g.id} className="flex items-start justify-between gap-3 p-3 border border-slate-100 dark:border-slate-800 rounded-md">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{g.contact?.name || '–'}</span>
                      <a href={`mailto:${g.contact?.email}`} className="text-xs text-slate-500 hover:text-orange-700 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {g.contact?.email}
                      </a>
                      {g.contact?.phone && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {g.contact.phone}
                        </span>
                      )}
                      {g.contact?.company && <span className="text-xs text-slate-400">· {g.contact.company}</span>}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {(g.viewed_ebook_ids || []).length} bekeken · {(g.downloaded_ebook_ids || []).length} downloads
                      · {new Date(g.created_at).toLocaleString('nl-NL')}
                    </p>
                  </div>
                  {g.lead_id && (
                    <a
                      href={`/leads/${g.lead_id}`}
                      className="text-xs text-orange-700 hover:underline whitespace-nowrap"
                    >
                      Naar lead →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking CTA */}
      <div className="space-y-3">
        <BookingCtaSettings
          cfg={bookingCfg}
          onChange={(field, value) => setBookingCfg((c) => ({ ...(c || {}), [field]: value }))}
        />
        <div className="flex justify-end">
          <Button
            onClick={handleSaveBookingCfg}
            disabled={savingBooking || !bookingCfg}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="ebook-booking-save-btn"
          >
            {savingBooking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Booking CTA opslaan
          </Button>
        </div>
      </div>

      {/* Modal: add/edit ebook */}
      {showForm && (
        <EbookFormModal
          ebookId={editingId}
          existing={editingId ? ebooks.find(e => e.id === editingId) : null}
          onClose={() => { setShowForm(false); setEditingId(null); }}
          onSaved={async () => {
            setShowForm(false);
            setEditingId(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function EbookRow({ book, onEdit, onDelete, onToggleActive }) {
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const cover = book.cover_url
    ? (book.cover_url.startsWith('http') ? book.cover_url : `${apiBase}${book.cover_url}`)
    : null;

  return (
    <div className="flex items-start gap-3 py-3" data-testid={`ebook-row-${book.id}`}>
      {/* Thumb */}
      <div className="flex-shrink-0 w-14 h-20 rounded-md overflow-hidden bg-gradient-to-br from-orange-100 to-amber-50 flex items-center justify-center border border-slate-200">
        {cover ? (
          <img src={cover} alt={book.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <BookOpen className="w-6 h-6 text-orange-400" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-semibold text-sm truncate" title={book.title}>{book.title}</h4>
          {book.is_paid ? (
            <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">
              <Tag className="w-2.5 h-2.5 mr-1" /> {formatEUR(book.price)}
            </Badge>
          ) : (
            <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">Gratis</Badge>
          )}
          {!book.active && (
            <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-300">Inactief</Badge>
          )}
          {(book.pdf_exists === false || book.cover_exists === false) && (
            <Badge
              className="bg-red-100 text-red-800 border-0 text-[10px]"
              title={`${book.pdf_exists === false ? 'PDF ontbreekt in MongoDB. ' : ''}${book.cover_exists === false ? 'Cover ontbreekt in MongoDB. ' : ''}Open Bewerken en upload het bestand opnieuw.`}
              data-testid={`ebook-missing-files-${book.id}`}
            >
              ⚠️ {book.pdf_exists === false && book.cover_exists === false ? 'PDF + cover ontbreken' : book.pdf_exists === false ? 'PDF ontbreekt' : 'Cover ontbreekt'}
            </Badge>
          )}
        </div>
        {book.description && (
          <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{book.description}</p>
        )}
        <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-1 flex-wrap">
          {book.author && <span>{book.author}</span>}
          {book.author && <span>·</span>}
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {book.view_count || 0}</span>
          <span className="flex items-center gap-1"><DownloadIcon className="w-3 h-3" /> {book.download_count || 0}</span>
          {book.file_size && <><span>·</span><span>{formatBytes(book.file_size)}</span></>}
          {book.pages && <><span>·</span><span>{book.pages} pag.</span></>}
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <Switch
          checked={!!book.active}
          onCheckedChange={onToggleActive}
          aria-label="Actief"
        />
        <Button size="sm" variant="ghost" onClick={onEdit} className="h-8 w-8 p-0" title="Bewerken">
          <Edit3 className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} className="h-8 w-8 p-0 text-slate-400 hover:text-red-600" title="Verwijderen">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function EbookFormModal({ ebookId, existing, onClose, onSaved }) {
  const isEdit = !!ebookId;
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const [form, setForm] = useState(() => ({
    title: existing?.title || '',
    description: existing?.description || '',
    author: existing?.author || '',
    pages: existing?.pages || '',
    price: existing?.price || 0,
    is_paid: !!existing?.is_paid,
    active: existing?.active !== false,
    cover_url: existing?.cover_url || '',
    cover_file_id: existing?.cover_file_id || '',
    pdf_url: existing?.pdf_url || '',
    pdf_file_id: existing?.pdf_file_id || existing?.pdf_filename || '',
    pdf_filename: existing?.pdf_filename || existing?.pdf_file_id || '',
    pdf_original_name: existing?.pdf_original_name || '',
    file_size: existing?.file_size || null,
  }));

  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);   // 0..100
  const [uploadingCover, setUploadingCover] = useState(false);
  const [saving, setSaving] = useState(false);

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Alleen PDF-bestanden toegestaan.');
      return;
    }
    const MAX_TOTAL = 100 * 1024 * 1024;
    if (file.size > MAX_TOTAL) {
      toast.error(`PDF te groot. Max ${MAX_TOTAL / (1024 * 1024)} MB.`);
      return;
    }

    setUploadingPdf(true);
    setPdfProgress(0);
    try {
      // Chunked upload: omzeilt ingress body limits en is robuust voor grote bestanden
      const CHUNK_SIZE = 1024 * 1024 * 1.5; // 1.5 MB per chunk
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

      // 1. Init
      const initRes = await ebookLibraryAPI.adminUploadPdfInit({
        filename: file.name,
        total_size: file.size,
        total_chunks: totalChunks,
      });
      const uploadId = initRes.data?.upload_id;
      if (!uploadId) throw new Error('Geen upload_id ontvangen');

      // 2. Upload chunks sequentieel
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', String(i));
        fd.append('file', blob, `chunk_${i}`);
        await ebookLibraryAPI.adminUploadPdfChunk(fd);
        setPdfProgress(Math.round(((i + 1) / totalChunks) * 95)); // 95% voor chunks, 5% voor finalize
      }

      // 3. Finalize
      const finRes = await ebookLibraryAPI.adminUploadPdfFinalize({
        upload_id: uploadId,
        total_chunks: totalChunks,
        expected_size: file.size,
      });
      const data = finRes.data || {};
      if (data.file_size !== file.size) {
        toast.warning(`Bestandsgrootte op server (${data.file_size}) verschilt van origineel (${file.size}).`);
      }
      setForm(f => ({
        ...f,
        pdf_url: data.url,
        pdf_file_id: data.file_id || data.filename,
        pdf_filename: data.file_id || data.filename,
        pdf_original_name: data.original_name || file.name,
        file_size: data.file_size,
      }));
      setPdfProgress(100);
      toast.success(`PDF geüpload (${(data.file_size / (1024 * 1024)).toFixed(1)} MB).`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'PDF upload mislukt.';
      toast.error(`PDF upload mislukt: ${detail}`);
      setPdfProgress(0);
    } finally {
      setUploadingPdf(false);
      e.target.value = '';
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await ebookLibraryAPI.adminUploadCover(fd);
      setForm(f => ({
        ...f,
        cover_url: res.data?.url || '',
        cover_file_id: res.data?.file_id || res.data?.filename || '',
      }));
      toast.success('Cover geüpload.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cover upload mislukt.');
    } finally {
      setUploadingCover(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Titel is verplicht.');
      return;
    }
    if (!form.pdf_file_id && !form.pdf_filename) {
      toast.error('Upload eerst een PDF.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        author: form.author.trim() || null,
        pages: form.pages ? Number(form.pages) : null,
        price: Number(form.price || 0),
        is_paid: !!form.is_paid,
        active: !!form.active,
        cover_url: form.cover_url || null,
        cover_file_id: form.cover_file_id || null,
        pdf_url: form.pdf_url,
        pdf_file_id: form.pdf_file_id || form.pdf_filename,
        pdf_filename: form.pdf_file_id || form.pdf_filename,
        pdf_original_name: form.pdf_original_name,
        file_size: form.file_size,
      };
      if (isEdit) {
        await ebookLibraryAPI.adminUpdate(ebookId, payload);
        toast.success('Ebook bijgewerkt.');
      } else {
        await ebookLibraryAPI.adminCreate(payload);
        toast.success('Ebook aangemaakt.');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const coverPreview = form.cover_url
    ? (form.cover_url.startsWith('http') ? form.cover_url : `${apiBase}${form.cover_url}`)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="ebook-form-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800 sticky top-0 bg-white dark:bg-slate-900 z-10">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-orange-600" />
            {isEdit ? 'Ebook bewerken' : 'Nieuwe ebook'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1">
            <Trash2 className="w-5 h-5" style={{ display: 'none' }} />
            <span className="text-xl leading-none">×</span>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Title */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Titel <span className="text-red-500">*</span></Label>
            <Input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Bijv. 10 tips voor meer energie op de werkvloer"
              data-testid="ebook-title-input"
            />
          </div>

          {/* Author + Pages */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Auteur</Label>
              <Input
                value={form.author}
                onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
                placeholder="Optioneel"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Aantal pagina's</Label>
              <Input
                type="number"
                min="1"
                value={form.pages}
                onChange={e => setForm(f => ({ ...f, pages: e.target.value }))}
                placeholder="Optioneel"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Beschrijving</Label>
            <Textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Korte introductie die op de bibliotheek-kaart komt te staan…"
              className="min-h-[80px]"
              data-testid="ebook-description-input"
            />
          </div>

          {/* PDF upload */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-slate-500" /> PDF-bestand <span className="text-red-500">*</span>
            </Label>
            {form.pdf_filename && !uploadingPdf ? (
              <div className="flex items-center gap-2 p-2.5 border border-emerald-200 bg-emerald-50/50 rounded-md text-sm">
                <FileText className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                <span className="flex-1 truncate">{form.pdf_original_name || form.pdf_filename}</span>
                <span className="text-[11px] text-slate-500 whitespace-nowrap">{formatBytes(form.file_size)}</span>
                <label className="text-xs text-orange-700 hover:underline cursor-pointer whitespace-nowrap">
                  Vervangen
                  <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={handlePdfUpload} />
                </label>
              </div>
            ) : uploadingPdf ? (
              <div className="p-4 border-2 border-orange-300 bg-orange-50 rounded-md">
                <div className="flex items-center gap-2 text-sm text-orange-800 mb-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>PDF uploaden in stukjes… {pdfProgress}%</span>
                </div>
                <div className="w-full bg-orange-200/60 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-orange-600 h-full transition-all duration-200"
                    style={{ width: `${pdfProgress}%` }}
                  />
                </div>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50/40 rounded-md text-sm cursor-pointer transition-colors">
                <Upload className="w-4 h-4 text-slate-500" /> Sleep een PDF hierheen of klik om te uploaden (max 100MB)
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={handlePdfUpload} data-testid="ebook-pdf-upload" />
              </label>
            )}
          </div>

          {/* Cover upload */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <ImageIcon className="w-4 h-4 text-slate-500" /> Cover-afbeelding (optioneel)
            </Label>
            <div className="flex items-start gap-3">
              {coverPreview ? (
                <img src={coverPreview} alt="cover" className="w-20 h-28 object-cover rounded-md border border-slate-200" />
              ) : (
                <div className="w-20 h-28 bg-gradient-to-br from-orange-50 to-amber-50 rounded-md border border-dashed border-slate-300 flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-slate-300" />
                </div>
              )}
              <div className="flex-1">
                <label className={`inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-md cursor-pointer ${uploadingCover ? 'border-orange-300 bg-orange-50' : 'border-slate-300 hover:bg-slate-50'}`}>
                  {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {coverPreview ? 'Vervang cover' : 'Upload cover'}
                  <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} data-testid="ebook-cover-upload" />
                </label>
                {coverPreview && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, cover_url: '', cover_file_id: '' }))}
                    className="ml-2 text-xs text-slate-500 hover:text-red-600"
                  >
                    Verwijder
                  </button>
                )}
                <p className="text-[11px] text-slate-400 mt-2">JPG, PNG of WebP. Max 8MB. Aanbevolen formaat: 600×800px.</p>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="border border-slate-200 dark:border-slate-800 rounded-md p-3 space-y-3 bg-slate-50/40 dark:bg-slate-800/20">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <Tag className="w-4 h-4 text-slate-500" /> Betaalde ebook
              </Label>
              <Switch
                checked={form.is_paid}
                onCheckedChange={(v) => setForm(f => ({ ...f, is_paid: v, price: v ? (f.price || 9.99) : 0 }))}
                data-testid="ebook-is-paid-switch"
              />
            </div>
            {form.is_paid && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Prijs (EUR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                  placeholder="9.99"
                  className="max-w-[200px]"
                />
                <p className="text-[11px] text-amber-700 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" /> Checkout-flow is nog niet actief — gebruikers zien een "Koop voor €X" knop maar kunnen nog niet betalen.
                </p>
              </div>
            )}
          </div>

          {/* Active */}
          <div className="flex items-center justify-between gap-2 border-t border-slate-100 dark:border-slate-800 pt-3">
            <Label className="text-sm font-medium">Actief (zichtbaar in publieke bibliotheek)</Label>
            <Switch
              checked={form.active}
              onCheckedChange={(v) => setForm(f => ({ ...f, active: v }))}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuleren</Button>
          <Button
            onClick={handleSave}
            disabled={saving || uploadingPdf || uploadingCover}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="ebook-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {isEdit ? 'Bijwerken' : 'Opslaan'}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// VITALITEIT BENCHMARK MANAGER
// ═══════════════════════════════════════════════════════════════
function VitaliteitBenchmarkManager() {
  const [cfg, setCfg] = useState(null);
  const [defaultBenchmarks, setDefaultBenchmarks] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await vitaliteitBenchmarkAPI.adminGetConfig();
      const data = res.data || {};
      setCfg({
        active: data.active !== false,
        intro_title: data.intro_title || '',
        intro_subtitle: data.intro_subtitle || '',
        intro_badge: data.intro_badge || '',
        cta_start: data.cta_start || '',
        cta_submit: data.cta_submit || '',
        accent_color: data.accent_color || '#10b981',
        show_pdf_download: data.show_pdf_download !== false,
        roi_label: data.roi_label || '',
        working_days_per_year: data.working_days_per_year || 230,
        fte_help_title: data.fte_help_title || '',
        fte_help_explainer: data.fte_help_explainer || '',
        thank_you_message: data.thank_you_message || '',
        sector_overrides: data.sector_overrides || {},
      });
      setDefaultBenchmarks(data.default_benchmarks || {});
    } catch (e) {
      const status = e?.response?.status;
      let msg = 'Kon configuratie niet laden.';
      if (status === 401) msg = 'Je sessie is verlopen. Log opnieuw in om de tool te beheren.';
      else if (status === 403) msg = 'Je hebt geen rechten om deze tool te beheren (admin/settings.edit_general vereist).';
      else if (status >= 500) msg = 'Server-fout bij laden. Probeer het later opnieuw.';
      else if (e?.message?.includes('Network')) msg = 'Geen verbinding met de server. Controleer je internetverbinding.';
      // eslint-disable-next-line no-console
      console.error('[Vitaliteit Benchmark] adminGetConfig faalde:', status, e?.response?.data || e?.message);
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    setSubmissionsLoading(true);
    try {
      const res = await vitaliteitBenchmarkAPI.adminListSubmissions(50);
      setSubmissions(res.data?.submissions || []);
    } catch (e) {
      // niet kritiek
    } finally {
      setSubmissionsLoading(false);
    }
  };

  useEffect(() => { load(); loadSubmissions(); }, []);

  const handleSave = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      // Strip lege override-objecten zodat de standaard wordt gebruikt
      const cleaned = {};
      Object.entries(cfg.sector_overrides || {}).forEach(([sector, ov]) => {
        const stripped = {};
        ['verzuim', 'budget', 'burnout', 'dagKosten'].forEach(k => {
          if (ov && ov[k] !== '' && ov[k] !== null && ov[k] !== undefined && !Number.isNaN(Number(ov[k]))) {
            stripped[k] = Number(ov[k]);
          }
        });
        if (Object.keys(stripped).length > 0) cleaned[sector] = stripped;
      });
      const payload = withBookingFields({
        active: cfg.active,
        intro_title: cfg.intro_title,
        intro_subtitle: cfg.intro_subtitle,
        intro_badge: cfg.intro_badge,
        cta_start: cfg.cta_start,
        cta_submit: cfg.cta_submit,
        accent_color: cfg.accent_color,
        show_pdf_download: cfg.show_pdf_download,
        roi_label: cfg.roi_label,
        working_days_per_year: Number(cfg.working_days_per_year) || 230,
        fte_help_title: cfg.fte_help_title,
        fte_help_explainer: cfg.fte_help_explainer,
        thank_you_message: cfg.thank_you_message,
        sector_overrides: cleaned,
      }, cfg);
      await vitaliteitBenchmarkAPI.adminUpdateConfig(payload);
      toast.success('Configuratie opgeslagen.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Weet je zeker dat je alle teksten EN sector-overrides wilt resetten naar de standaard?')) return;
    setSaving(true);
    try {
      await vitaliteitBenchmarkAPI.adminResetConfig();
      toast.success('Standaard-config hersteld.');
      await load();
    } catch (e) {
      toast.error('Reset mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const embedUrl = `${window.location.origin}/vitaliteit-benchmark`;
  const embedSnippet = `<iframe src="${embedUrl}" width="100%" height="1100" frameborder="0" style="border:0;"></iframe>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopiedEmbed(true);
      toast.success('Embed-code gekopieerd.');
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch (e) {
      toast.error('Kopiëren mislukt.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!cfg) {
    return (
      <Card>
        <CardContent className="p-8 text-center space-y-3">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
          <h3 className="font-semibold text-lg">Configuratie niet beschikbaar</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {loadError || 'Er ging iets mis bij het laden van de Vitaliteit Benchmark configuratie.'}
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button onClick={() => { load(); loadSubmissions(); }} variant="outline">
              <RotateCcw className="w-4 h-4 mr-2" /> Opnieuw proberen
            </Button>
            {loadError && loadError.includes('sessie') && (
              <Button onClick={() => { window.location.href = '/login'; }} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Inloggen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="vitaliteit-benchmark-manager">
      {/* Embed & preview */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-emerald-600" /> Embed & preview
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Deel deze link of plak de embed-code op je website. Iedere submit wordt automatisch een lead in het CRM.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <Input readOnly value={embedUrl} className="bg-muted/40 text-xs" data-testid="vb-embed-url" />
                <a href={embedUrl} target="_blank" rel="noreferrer" className="px-3 py-2 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap">
                  Open in nieuw tabblad
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Input readOnly value={embedSnippet} className="bg-muted/40 text-xs font-mono" />
                <Button variant="outline" size="sm" onClick={copyEmbed} className="whitespace-nowrap" data-testid="vb-copy-embed-btn">
                  {copiedEmbed ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copiedEmbed ? 'Gekopieerd' : 'Kopieer'}
                </Button>
              </div>
            </div>
            <div className="flex flex-col items-start gap-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
              <div className="flex items-center gap-2">
                <Switch
                  checked={cfg.active}
                  onCheckedChange={(v) => setCfg(c => ({ ...c, active: v }))}
                  data-testid="vb-active-switch"
                />
                <span className="text-sm">{cfg.active ? 'Actief' : 'Gepauzeerd'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Intro / teksten */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-base flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-emerald-600" /> Teksten & branding
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Intro badge</Label>
              <Input value={cfg.intro_badge} onChange={e => setCfg(c => ({ ...c, intro_badge: e.target.value }))} data-testid="vb-intro-badge" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Accent-kleur (hex)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={cfg.accent_color || '#10b981'}
                  onChange={e => setCfg(c => ({ ...c, accent_color: e.target.value }))}
                  className="w-12 h-9 p-1"
                />
                <Input
                  value={cfg.accent_color || ''}
                  onChange={e => setCfg(c => ({ ...c, accent_color: e.target.value }))}
                  placeholder="#10b981"
                  className="text-sm"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Intro titel</Label>
            <Input value={cfg.intro_title} onChange={e => setCfg(c => ({ ...c, intro_title: e.target.value }))} data-testid="vb-intro-title" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Intro subtitel</Label>
            <Textarea
              value={cfg.intro_subtitle}
              onChange={e => setCfg(c => ({ ...c, intro_subtitle: e.target.value }))}
              className="min-h-[80px]"
              data-testid="vb-intro-subtitle"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">CTA-tekst (start)</Label>
              <Input value={cfg.cta_start} onChange={e => setCfg(c => ({ ...c, cta_start: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">CTA-tekst (verzenden)</Label>
              <Input value={cfg.cta_submit} onChange={e => setCfg(c => ({ ...c, cta_submit: e.target.value }))} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Bedank-bericht (na submit)</Label>
            <Input value={cfg.thank_you_message} onChange={e => setCfg(c => ({ ...c, thank_you_message: e.target.value }))} />
          </div>
        </CardContent>
      </Card>

      {/* FTE-help & ROI */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" /> Berekening & uitleg
          </h3>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">ROI-label op dashboard</Label>
              <Input value={cfg.roi_label} onChange={e => setCfg(c => ({ ...c, roi_label: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Werkdagen per jaar</Label>
              <Input
                type="number" min="1" max="366"
                value={cfg.working_days_per_year}
                onChange={e => setCfg(c => ({ ...c, working_days_per_year: e.target.value }))}
              />
              <p className="text-[11px] text-slate-400">Gebruikt in ROI-berekening (standaard 230).</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">PDF-download tonen</Label>
              <div className="flex items-center gap-2 h-9">
                <Switch
                  checked={cfg.show_pdf_download}
                  onCheckedChange={(v) => setCfg(c => ({ ...c, show_pdf_download: v }))}
                />
                <span className="text-sm">{cfg.show_pdf_download ? 'Ja' : 'Nee'}</span>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">FTE-help titel</Label>
            <Input value={cfg.fte_help_title} onChange={e => setCfg(c => ({ ...c, fte_help_title: e.target.value }))} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">FTE-help uitleg</Label>
            <Textarea
              value={cfg.fte_help_explainer}
              onChange={e => setCfg(c => ({ ...c, fte_help_explainer: e.target.value }))}
              className="min-h-[70px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sector benchmarks overrides */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-base flex items-center gap-2">
                <Building2 className="w-4 h-4 text-emerald-600" /> Sector benchmarks
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Pas waarden per sector aan. Leeg = standaard TNO/CBS-cijfer wordt gebruikt.
              </p>
            </div>
            <Badge variant="secondary">{Object.keys(defaultBenchmarks).length} sectoren</Badge>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-slate-600">Sector</th>
                  <th className="px-3 py-2 font-semibold text-slate-600"><Activity className="inline w-3 h-3 mr-1" />Verzuim (%)</th>
                  <th className="px-3 py-2 font-semibold text-slate-600"><Euro className="inline w-3 h-3 mr-1" />Budget/FTE</th>
                  <th className="px-3 py-2 font-semibold text-slate-600"><Heart className="inline w-3 h-3 mr-1" />Burn-out (%)</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Dagkosten</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.entries(defaultBenchmarks).map(([sector, def]) => {
                  const ov = cfg.sector_overrides?.[sector] || {};
                  const setField = (field, val) => setCfg(c => ({
                    ...c,
                    sector_overrides: {
                      ...(c.sector_overrides || {}),
                      [sector]: { ...((c.sector_overrides || {})[sector] || {}), [field]: val },
                    },
                  }));
                  const cell = (field, step = '0.1') => (
                    <td className="px-2 py-1.5">
                      <Input
                        type="number" step={step} min="0"
                        value={ov[field] ?? ''}
                        onChange={e => setField(field, e.target.value)}
                        placeholder={String(def[field])}
                        className="h-7 text-xs"
                        data-testid={`vb-bm-${sector.split(':')[0]}-${field}`}
                      />
                    </td>
                  );
                  return (
                    <tr key={sector} className="hover:bg-slate-50/50">
                      <td className="px-3 py-1.5 font-medium text-slate-700 whitespace-nowrap">{sector}</td>
                      {cell('verzuim', '0.1')}
                      {cell('budget', '5')}
                      {cell('burnout', '1')}
                      {cell('dagKosten', '5')}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recente submissions */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-slate-500" /> Recente submissions
              <Badge variant="secondary" className="ml-1">{submissions.length}</Badge>
            </h3>
            <Button size="sm" variant="ghost" onClick={loadSubmissions} disabled={submissionsLoading}>
              {submissionsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vernieuwen'}
            </Button>
          </div>
          {submissions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nog geen submissions.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {submissions.map((s) => (
                <div key={s.id} className="flex items-start justify-between gap-3 p-3 border border-slate-100 dark:border-slate-800 rounded-md">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{s.contact?.name || '–'}</span>
                      <a href={`mailto:${s.contact?.email}`} className="text-xs text-slate-500 hover:text-emerald-700 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {s.contact?.email}
                      </a>
                      <span className="text-xs text-slate-400">· {s.company}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1 truncate">
                      {s.sector} · {Number(s.fte).toFixed(1)} FTE · verzuim {Number(s.verzuim_pct).toFixed(1)}%
                    </p>
                    <p className="text-[11px] text-emerald-700 font-semibold mt-1">
                      ROI: €{Math.round(s.results?.potential_savings || 0).toLocaleString('nl-NL')}
                      <span className="text-slate-400 font-normal ml-2">{new Date(s.created_at).toLocaleString('nl-NL')}</span>
                    </p>
                  </div>
                  {s.lead_id && (
                    <a href={`/leads/${s.lead_id}`} className="text-xs text-emerald-700 hover:underline whitespace-nowrap">
                      Naar lead →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking CTA */}
      <BookingCtaSettings
        cfg={cfg}
        onChange={(field, value) => setCfg((c) => ({ ...c, [field]: value }))}
      />

      {/* Actions */}
      <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-border py-3 -mx-1 px-1 flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" onClick={handleReset} disabled={saving} data-testid="vb-reset-btn">
          <RotateCcw className="w-4 h-4 mr-1" /> Standaard herstellen
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="vb-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Opslaan
        </Button>
      </div>
    </div>
  );
}


// ─── Arbeidsvoorwaarden Check Manager ──────────────────────────────────────
function ArbeidsvoorwaardenCheckManager() {
  const [cfg, setCfg] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistDirty, setChecklistDirty] = useState(false);
  const [usingCustomChecklist, setUsingCustomChecklist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await arbeidsvoorwaardenCheckAPI.adminGetConfig();
      const data = res.data || {};
      setCfg({
        active: data.active !== false,
        intro_badge: data.intro_badge || '',
        intro_title: data.intro_title || '',
        intro_subtitle: data.intro_subtitle || '',
        cta_start: data.cta_start || '',
        cta_submit: data.cta_submit || '',
        accent_color: data.accent_color || '#10b981',
        show_pdf_download: data.show_pdf_download !== false,
        thank_you_message: data.thank_you_message || '',
        benchmark_mental: Number(data.benchmark_mental ?? 85),
        benchmark_flexibility: Number(data.benchmark_flexibility ?? 90),
        benchmark_physical: Number(data.benchmark_physical ?? 65),
        weight_mental: Number(data.weight_mental ?? 3),
        weight_flexibility: Number(data.weight_flexibility ?? 3),
        weight_physical: Number(data.weight_physical ?? 1.5),
      });
      setChecklistItems(data.checklist_items || []);
      setUsingCustomChecklist(!!data.using_custom_checklist);
      setChecklistDirty(false);
    } catch (e) {
      const status = e?.response?.status;
      let msg = 'Kon configuratie niet laden.';
      if (status === 401) msg = 'Je sessie is verlopen. Log opnieuw in om de tool te beheren.';
      else if (status === 403) msg = 'Je hebt geen rechten om deze tool te beheren (admin/settings.edit_general vereist).';
      else if (status >= 500) msg = 'Server-fout bij laden. Probeer het later opnieuw.';
      else if (e?.message?.includes('Network')) msg = 'Geen verbinding met de server. Controleer je internetverbinding.';
      // eslint-disable-next-line no-console
      console.error('[Arbeidsvoorwaarden Check] adminGetConfig faalde:', status, e?.response?.data || e?.message);
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    setSubmissionsLoading(true);
    try {
      const res = await arbeidsvoorwaardenCheckAPI.adminListSubmissions(50);
      setSubmissions(res.data?.submissions || []);
    } catch (e) {
      // niet kritiek
    } finally {
      setSubmissionsLoading(false);
    }
  };

  useEffect(() => { load(); loadSubmissions(); }, []);

  const handleSave = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = withBookingFields({
        active: cfg.active,
        intro_badge: cfg.intro_badge,
        intro_title: cfg.intro_title,
        intro_subtitle: cfg.intro_subtitle,
        cta_start: cfg.cta_start,
        cta_submit: cfg.cta_submit,
        accent_color: cfg.accent_color,
        show_pdf_download: cfg.show_pdf_download,
        thank_you_message: cfg.thank_you_message,
        benchmark_mental: Number(cfg.benchmark_mental),
        benchmark_flexibility: Number(cfg.benchmark_flexibility),
        benchmark_physical: Number(cfg.benchmark_physical),
        weight_mental: Number(cfg.weight_mental),
        weight_flexibility: Number(cfg.weight_flexibility),
        weight_physical: Number(cfg.weight_physical),
      }, cfg);
      if (checklistDirty || usingCustomChecklist) {
        // Stuur alleen mee als admin items heeft bewerkt OF al een custom-lijst actief is
        payload.checklist_items = checklistItems.map((it) => ({
          id: it.id,
          category: it.category,
          label: it.label || '',
          desc: it.desc || '',
          advice: it.advice || '',
        }));
      }
      await arbeidsvoorwaardenCheckAPI.adminUpdateConfig(payload);
      toast.success('Configuratie opgeslagen.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Weet je zeker dat je alle teksten en benchmark-waarden wilt resetten naar de standaard?')) return;
    setSaving(true);
    try {
      await arbeidsvoorwaardenCheckAPI.adminResetConfig();
      toast.success('Standaard-config hersteld.');
      await load();
    } catch (e) {
      toast.error('Reset mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const embedUrl = `${window.location.origin}/arbeidsvoorwaarden-check`;
  const embedSnippet = `<iframe src="${embedUrl}" width="100%" height="1200" frameborder="0" style="border:0;"></iframe>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopiedEmbed(true);
      toast.success('Embed-code gekopieerd.');
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch (e) {
      toast.error('Kopiëren mislukt.');
    }
  };

  // ── Checklist editor helpers ──
  const updateChecklistItem = (idx, field, value) => {
    setChecklistItems((arr) => {
      const next = arr.slice();
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setChecklistDirty(true);
  };

  const addChecklistItem = (category) => {
    const tempId = `new_${Math.random().toString(36).slice(2, 10)}`;
    setChecklistItems((arr) => [
      ...arr,
      { id: tempId, category, label: '', desc: '', advice: '' },
    ]);
    setChecklistDirty(true);
  };

  const removeChecklistItem = (idx) => {
    if (!window.confirm('Dit item verwijderen?')) return;
    setChecklistItems((arr) => arr.filter((_, i) => i !== idx));
    setChecklistDirty(true);
  };

  const moveChecklistItem = (idx, dir) => {
    setChecklistItems((arr) => {
      const next = arr.slice();
      const j = idx + dir;
      if (j < 0 || j >= next.length) return arr;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setChecklistDirty(true);
  };

  const handleResetChecklist = async () => {
    if (!window.confirm('Alle items terugzetten naar de standaard 19-item lijst? Lokale wijzigingen gaan verloren.')) return;
    setSaving(true);
    try {
      const res = await arbeidsvoorwaardenCheckAPI.adminResetChecklist();
      const data = res.data || {};
      setChecklistItems(data.checklist_items || []);
      setUsingCustomChecklist(false);
      setChecklistDirty(false);
      toast.success('Standaard checklist hersteld.');
    } catch (e) {
      toast.error('Reset checklist mislukt.');
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!cfg) {
    return (
      <Card>
        <CardContent className="p-8 text-center space-y-3">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
          <h3 className="font-semibold text-lg">Configuratie niet beschikbaar</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {loadError || 'Er ging iets mis bij het laden van de Arbeidsvoorwaarden Check configuratie.'}
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button onClick={() => { load(); loadSubmissions(); }} variant="outline">
              <RotateCcw className="w-4 h-4 mr-2" /> Opnieuw proberen
            </Button>
            {loadError && loadError.includes('sessie') && (
              <Button onClick={() => { window.location.href = '/login'; }} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Inloggen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const itemCounts = {
    mental: checklistItems.filter(i => i.category === 'mental').length,
    flexibility: checklistItems.filter(i => i.category === 'flexibility').length,
    physical: checklistItems.filter(i => i.category === 'physical').length,
  };

  return (
    <div className="space-y-5" data-testid="arbeidsvoorwaarden-check-manager">
      {/* Embed & status */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-emerald-600" /> Embed & preview
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Deel deze link of plak de embed-code op je website. Iedere submit wordt automatisch een lead in het CRM.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <Input readOnly value={embedUrl} className="bg-muted/40 text-xs" data-testid="av-embed-url" />
                <a href={embedUrl} target="_blank" rel="noreferrer" className="px-3 py-2 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap">
                  Open in nieuw tabblad
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Input readOnly value={embedSnippet} className="bg-muted/40 text-xs font-mono" />
                <Button variant="outline" size="sm" onClick={copyEmbed} className="whitespace-nowrap" data-testid="av-copy-embed-btn">
                  {copiedEmbed ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copiedEmbed ? 'Gekopieerd' : 'Kopieer'}
                </Button>
              </div>
            </div>
            <div className="flex flex-col items-start gap-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
              <div className="flex items-center gap-2">
                <Switch
                  checked={cfg.active}
                  onCheckedChange={(v) => setCfg(c => ({ ...c, active: v }))}
                  data-testid="av-active-switch"
                />
                <span className="text-sm">{cfg.active ? 'Actief' : 'Gepauzeerd'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Teksten & branding */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-base flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-emerald-600" /> Teksten & branding
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Intro badge</Label>
              <Input value={cfg.intro_badge} onChange={e => setCfg(c => ({ ...c, intro_badge: e.target.value }))} data-testid="av-intro-badge" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Accent-kleur (hex)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={cfg.accent_color || '#10b981'}
                  onChange={e => setCfg(c => ({ ...c, accent_color: e.target.value }))}
                  className="w-12 h-9 p-1"
                />
                <Input
                  value={cfg.accent_color || ''}
                  onChange={e => setCfg(c => ({ ...c, accent_color: e.target.value }))}
                  placeholder="#10b981"
                  className="text-sm"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Intro titel</Label>
            <Input value={cfg.intro_title} onChange={e => setCfg(c => ({ ...c, intro_title: e.target.value }))} data-testid="av-intro-title" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Intro subtitel (War on Talent verhaal)</Label>
            <Textarea
              value={cfg.intro_subtitle}
              onChange={e => setCfg(c => ({ ...c, intro_subtitle: e.target.value }))}
              className="min-h-[100px]"
              data-testid="av-intro-subtitle"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">CTA-tekst (start)</Label>
              <Input value={cfg.cta_start} onChange={e => setCfg(c => ({ ...c, cta_start: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">CTA-tekst (toon scorecard)</Label>
              <Input value={cfg.cta_submit} onChange={e => setCfg(c => ({ ...c, cta_submit: e.target.value }))} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Bedank-bericht (na submit)</Label>
              <Input value={cfg.thank_you_message} onChange={e => setCfg(c => ({ ...c, thank_you_message: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">PDF-download tonen</Label>
              <div className="flex items-center gap-2 h-9">
                <Switch
                  checked={cfg.show_pdf_download}
                  onCheckedChange={(v) => setCfg(c => ({ ...c, show_pdf_download: v }))}
                />
                <span className="text-sm">{cfg.show_pdf_download ? 'Ja' : 'Nee'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gen Z Benchmark percentages */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-600" /> Gen Z Benchmark 2026
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Het percentage talent dat dit als harde eis stelt. Wordt getoond als referentielijn op het dashboard en in de PDF.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Mentale Gezondheid (%)</Label>
              <Input
                type="number" min="0" max="100"
                value={cfg.benchmark_mental}
                onChange={e => setCfg(c => ({ ...c, benchmark_mental: e.target.value }))}
                data-testid="av-bench-mental"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Flexibiliteit (%)</Label>
              <Input
                type="number" min="0" max="100"
                value={cfg.benchmark_flexibility}
                onChange={e => setCfg(c => ({ ...c, benchmark_flexibility: e.target.value }))}
                data-testid="av-bench-flex"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fysiek & Omgeving (%)</Label>
              <Input
                type="number" min="0" max="100"
                value={cfg.benchmark_physical}
                onChange={e => setCfg(c => ({ ...c, benchmark_physical: e.target.value }))}
                data-testid="av-bench-phys"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weegfactoren */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" /> Puntensysteem - weegfactoren
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Bepaalt hoe zwaar items per categorie meetellen in de Wellness Grade. Mentaal en flex moeten anno 2026 zwaarder wegen dan fysiek.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Gewicht Mentaal (×)</Label>
              <Input
                type="number" step="0.5" min="0" max="10"
                value={cfg.weight_mental}
                onChange={e => setCfg(c => ({ ...c, weight_mental: e.target.value }))}
                data-testid="av-weight-mental"
              />
              <p className="text-[11px] text-slate-400">{itemCounts.mental} items in deze categorie</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Gewicht Flexibiliteit (×)</Label>
              <Input
                type="number" step="0.5" min="0" max="10"
                value={cfg.weight_flexibility}
                onChange={e => setCfg(c => ({ ...c, weight_flexibility: e.target.value }))}
                data-testid="av-weight-flex"
              />
              <p className="text-[11px] text-slate-400">{itemCounts.flexibility} items in deze categorie</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Gewicht Fysiek (×)</Label>
              <Input
                type="number" step="0.5" min="0" max="10"
                value={cfg.weight_physical}
                onChange={e => setCfg(c => ({ ...c, weight_physical: e.target.value }))}
                data-testid="av-weight-phys"
              />
              <p className="text-[11px] text-slate-400">{itemCounts.physical} items in deze categorie</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Checklist editor */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-base flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-emerald-600" /> Checklist items
                {checklistDirty && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700 ml-1 text-[10px]">Niet opgeslagen</Badge>
                )}
                {usingCustomChecklist && !checklistDirty && (
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 ml-1 text-[10px]">Custom lijst</Badge>
                )}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {checklistItems.length} items in 3 categorieën. Bewerk labels, omschrijvingen en advies. Voeg toe, verwijder of sorteer. Vergeet niet onderaan op &quot;Opslaan&quot; te klikken.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary">{checklistItems.length} items</Badge>
              <Button size="sm" variant="outline" onClick={handleResetChecklist} disabled={saving} data-testid="av-reset-checklist-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Items resetten
              </Button>
            </div>
          </div>

          {['mental', 'flexibility', 'physical'].map((cat) => {
            const catLabel = cat === 'mental' ? 'Mentale Gezondheid' : cat === 'flexibility' ? 'Flexibiliteit' : 'Fysiek & Omgeving';
            const catColor = cat === 'mental' ? 'bg-rose-100 text-rose-700 border-rose-200'
              : cat === 'flexibility' ? 'bg-sky-100 text-sky-700 border-sky-200'
              : 'bg-amber-100 text-amber-700 border-amber-200';
            const catEntries = checklistItems
              .map((it, i) => ({ it, i }))
              .filter(({ it }) => it.category === cat);
            const catWeight = cat === 'mental' ? cfg.weight_mental
              : cat === 'flexibility' ? cfg.weight_flexibility
              : cfg.weight_physical;

            return (
              <div key={cat} className="rounded-lg border border-slate-200">
                <div className={`px-4 py-2 rounded-t-lg border-b ${catColor} flex items-center justify-between flex-wrap gap-2`}>
                  <div className="text-sm font-semibold">
                    {catLabel} <span className="text-xs opacity-70">· {catEntries.length} items · gewicht ×{catWeight}</span>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    className="text-xs h-7"
                    onClick={() => addChecklistItem(cat)}
                    data-testid={`av-add-item-${cat}`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Item toevoegen
                  </Button>
                </div>
                <div className="p-3 space-y-3">
                  {catEntries.length === 0 && (
                    <p className="text-xs text-slate-400 italic text-center py-3">Geen items in deze categorie.</p>
                  )}
                  {catEntries.map(({ it, i }, localIdx) => (
                    <div key={it.id} className="rounded-md border border-slate-200 bg-white p-3 hover:border-slate-300 transition-colors">
                      <div className="flex items-start gap-2 mb-2">
                        <div className="flex flex-col gap-1 pt-1">
                          <button
                            type="button"
                            onClick={() => moveChecklistItem(i, -1)}
                            disabled={localIdx === 0 && catEntries[0].i === i}
                            className="w-6 h-6 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                            title="Omhoog"
                          >
                            <ChevronUp className="w-3.5 h-3.5 text-slate-500" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveChecklistItem(i, 1)}
                            disabled={i === checklistItems.length - 1}
                            className="w-6 h-6 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                            title="Omlaag"
                          >
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                          </button>
                        </div>
                        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1fr,170px] gap-2">
                          <Input
                            value={it.label || ''}
                            onChange={(e) => updateChecklistItem(i, 'label', e.target.value)}
                            placeholder="Label van het item"
                            className="text-sm font-medium"
                            data-testid={`av-item-label-${it.id}`}
                          />
                          <select
                            value={it.category}
                            onChange={(e) => updateChecklistItem(i, 'category', e.target.value)}
                            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                            data-testid={`av-item-cat-${it.id}`}
                          >
                            <option value="mental">Mentale Gezondheid (×3)</option>
                            <option value="flexibility">Flexibiliteit (×3)</option>
                            <option value="physical">Fysiek & Omgeving (×1.5)</option>
                          </select>
                        </div>
                        <Button
                          variant="ghost" size="icon"
                          className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 h-8 w-8"
                          onClick={() => removeChecklistItem(i)}
                          title="Verwijderen"
                          data-testid={`av-item-del-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="ml-8 space-y-2">
                        <Input
                          value={it.desc || ''}
                          onChange={(e) => updateChecklistItem(i, 'desc', e.target.value)}
                          placeholder="Korte omschrijving (wordt getoond onder de checkbox)"
                          className="text-xs"
                        />
                        <Textarea
                          value={it.advice || ''}
                          onChange={(e) => updateChecklistItem(i, 'advice', e.target.value)}
                          placeholder="Advies bij ontbrekend item (verschijnt in 'Gaps to Close' en PDF)"
                          className="text-xs min-h-[56px]"
                        />
                        <p className="text-[10px] text-slate-400 font-mono">ID: {it.id}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Recente submissions */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-slate-500" /> Recente submissions
              <Badge variant="secondary" className="ml-1">{submissions.length}</Badge>
            </h3>
            <Button size="sm" variant="ghost" onClick={loadSubmissions} disabled={submissionsLoading}>
              {submissionsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vernieuwen'}
            </Button>
          </div>
          {submissions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nog geen submissions.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {submissions.map((s) => (
                <div key={s.id} className="flex items-start justify-between gap-3 p-3 border border-slate-100 dark:border-slate-800 rounded-md">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{s.contact?.name || '–'}</span>
                      <a href={`mailto:${s.contact?.email}`} className="text-xs text-slate-500 hover:text-emerald-700 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {s.contact?.email}
                      </a>
                      <span className="text-xs text-slate-400">· {s.company}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1 truncate">
                      {s.sector} · {s.checked_items?.length || 0} items aangevinkt
                    </p>
                    <p className="text-[11px] text-emerald-700 font-semibold mt-1">
                      Wellness Grade: {s.score?.grade || '–'} ({s.score?.percentage || 0}%)
                      <span className="text-slate-400 font-normal ml-2">{new Date(s.created_at).toLocaleString('nl-NL')}</span>
                    </p>
                  </div>
                  {s.lead_id && (
                    <a href={`/leads/${s.lead_id}`} className="text-xs text-emerald-700 hover:underline whitespace-nowrap">
                      Naar lead →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking CTA */}
      <BookingCtaSettings
        cfg={cfg}
        onChange={(field, value) => setCfg((c) => ({ ...c, [field]: value }))}
      />

      {/* Actions */}
      <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-border py-3 -mx-1 px-1 flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" onClick={handleReset} disabled={saving} data-testid="av-reset-btn">
          <RotateCcw className="w-4 h-4 mr-1" /> Standaard herstellen
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="av-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Opslaan
        </Button>
      </div>
    </div>
  );
}


// ── Pomodoro Timer Manager ────────────────────────────────────────────
const PROFILE_PRESETS = [
  { key: 'standard', label: 'Standard' },
  { key: 'deep_work', label: 'Deep Work' },
  { key: 'custom', label: 'Custom' },
];
const INTERVENTION_TYPES = [
  { value: 'breathing', label: 'Ademhaling' },
  { value: 'stretch', label: 'Stretch' },
  { value: 'hydration', label: 'Hydratatie' },
  { value: 'movement', label: 'Beweging' },
  { value: 'mindset', label: 'Mindset' },
];

function PomodoroTimerManager() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [domainsText, setDomainsText] = useState('');

  const embedUrl = `${window.location.origin}/pomodoro-timer`;

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await pomodoroTimerAPI.adminGetConfig();
      setConfig(res.data);
      setDomainsText((res.data?.blocked_email_domains || []).join('\n'));
    } catch (e) {
      toast.error('Kon de Pomodoro Timer configuratie niet laden');
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await pomodoroTimerAPI.adminListSessions(50);
      setSessions(res.data?.sessions || []);
    } catch (e) {
      // stille fout
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    loadSessions();
  }, []);

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const blocked = domainsText
        .split(/[\n,;]+/)
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean);
      const payload = withBookingFields({
        active: config.active,
        intro_title: config.intro_title,
        intro_subtitle: config.intro_subtitle,
        intro_badge: config.intro_badge,
        gate_title: config.gate_title,
        gate_subtitle: config.gate_subtitle,
        cta_unlock: config.cta_unlock,
        accent_color: config.accent_color,
        sound_key: config.sound_key,
        profiles: config.profiles,
        interventions: config.interventions,
        blocked_email_domains: blocked,
        require_business_email: config.require_business_email,
      }, config);
      const res = await pomodoroTimerAPI.adminUpdateConfig(payload);
      setConfig(res.data);
      setDomainsText((res.data?.blocked_email_domains || []).join('\n'));
      toast.success('Pomodoro Timer opgeslagen');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Standaard instellingen herstellen? Alle aanpassingen gaan verloren.')) return;
    setSaving(true);
    try {
      const res = await pomodoroTimerAPI.adminResetDefaults();
      setConfig(res.data);
      setDomainsText((res.data?.blocked_email_domains || []).join('\n'));
      toast.success('Standaarden hersteld');
    } catch (e) {
      toast.error('Herstellen mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* noop */ }
  };

  const updateProfile = (idx, patch) => {
    setConfig((c) => {
      const profiles = [...(c.profiles || [])];
      profiles[idx] = { ...profiles[idx], ...patch };
      return { ...c, profiles };
    });
  };

  const updateIntervention = (idx, patch) => {
    setConfig((c) => {
      const ivs = [...(c.interventions || [])];
      ivs[idx] = { ...ivs[idx], ...patch };
      return { ...c, interventions: ivs };
    });
  };

  const removeIntervention = (idx) => {
    setConfig((c) => {
      const ivs = [...(c.interventions || [])];
      ivs.splice(idx, 1);
      return { ...c, interventions: ivs };
    });
  };

  const addIntervention = () => {
    setConfig((c) => ({
      ...c,
      interventions: [
        ...(c.interventions || []),
        { type: 'breathing', title: 'Nieuwe interventie', body: 'Korte instructie…', duration_sec: 60 },
      ],
    }));
  };

  const moveIntervention = (idx, dir) => {
    setConfig((c) => {
      const ivs = [...(c.interventions || [])];
      const j = idx + dir;
      if (j < 0 || j >= ivs.length) return c;
      [ivs[idx], ivs[j]] = [ivs[j], ivs[idx]];
      return { ...c, interventions: ivs };
    });
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="pomodoro-timer-manager">
      {/* Embed url */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Publieke URL</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input value={embedUrl} readOnly className="font-mono text-xs" data-testid="pomo-embed-url" />
                <Button variant="outline" size="icon" onClick={handleCopyEmbed} data-testid="pomo-copy-btn">
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href={embedUrl} target="_blank" rel="noopener noreferrer" data-testid="pomo-open-btn">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch
                checked={!!config.active}
                onCheckedChange={(v) => setConfig((c) => ({ ...c, active: v }))}
                data-testid="pomo-active-switch"
              />
              <Label className="text-sm">Tool actief</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Intro & Gate teksten */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-orange-600" /> Intro & toegangsscherm
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Badge tekst</Label>
              <Input value={config.intro_badge || ''} onChange={(e) => setConfig((c) => ({ ...c, intro_badge: e.target.value }))} data-testid="pomo-badge-input" />
            </div>
            <div>
              <Label className="text-xs">Accent kleur (hex)</Label>
              <Input value={config.accent_color || ''} onChange={(e) => setConfig((c) => ({ ...c, accent_color: e.target.value }))} placeholder="#ea580c" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Hoofdtitel</Label>
            <Input value={config.intro_title || ''} onChange={(e) => setConfig((c) => ({ ...c, intro_title: e.target.value }))} data-testid="pomo-intro-title-input" />
          </div>
          <div>
            <Label className="text-xs">Subtitel</Label>
            <Textarea rows={2} value={config.intro_subtitle || ''} onChange={(e) => setConfig((c) => ({ ...c, intro_subtitle: e.target.value }))} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Gate titel</Label>
              <Input value={config.gate_title || ''} onChange={(e) => setConfig((c) => ({ ...c, gate_title: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">CTA knop tekst</Label>
              <Input value={config.cta_unlock || ''} onChange={(e) => setConfig((c) => ({ ...c, cta_unlock: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Gate subtitel</Label>
            <Textarea rows={2} value={config.gate_subtitle || ''} onChange={(e) => setConfig((c) => ({ ...c, gate_subtitle: e.target.value }))} />
          </div>
        </CardContent>
      </Card>

      {/* Profielen */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Zap className="w-4 h-4 text-orange-600" /> Profielen
          </h3>
          <p className="text-xs text-muted-foreground">
            De gebruiker kiest één van deze profielen of stelt zelf focus/pauze in via 'Custom'.
          </p>
          {(config.profiles || []).map((p, i) => (
            <div key={p.key} className="border rounded-md p-3 grid sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-3">
                <Label className="text-xs">Label</Label>
                <Input value={p.label || ''} onChange={(e) => updateProfile(i, { label: e.target.value })} />
              </div>
              <div className="sm:col-span-5">
                <Label className="text-xs">Beschrijving</Label>
                <Input value={p.description || ''} onChange={(e) => updateProfile(i, { description: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Focus (min)</Label>
                <Input type="number" min="1" max="240" value={p.focus_min} onChange={(e) => updateProfile(i, { focus_min: Number(e.target.value) || 1 })} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Pauze (min)</Label>
                <Input type="number" min="1" max="120" value={p.break_min} onChange={(e) => updateProfile(i, { break_min: Number(e.target.value) || 1 })} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Audio + email validatie */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-orange-600" /> Geluid & toegang
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Alarm-geluid (loopt bij einde focus)</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <select
                  className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={config.sound_key || 'bell'}
                  onChange={(e) => setConfig((c) => ({ ...c, sound_key: e.target.value }))}
                  data-testid="pomo-sound-select"
                >
                  {SOUND_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <Button variant="outline" size="sm" onClick={() => previewSound(config.sound_key || 'bell')} type="button">
                  <Volume2 className="w-3.5 h-3.5 mr-1" /> Preview
                </Button>
              </div>
            </div>
            <div className="flex flex-col justify-end gap-1.5">
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!config.require_business_email}
                  onCheckedChange={(v) => setConfig((c) => ({ ...c, require_business_email: v }))}
                  data-testid="pomo-require-business-switch"
                />
                <Label className="text-sm">Vereis zakelijk e-mail</Label>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Als dit aan staat, worden onderstaande domeinen geweigerd.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-xs">Geblokkeerde e-maildomeinen</Label>
            <Textarea
              rows={5}
              value={domainsText}
              onChange={(e) => setDomainsText(e.target.value)}
              placeholder={'gmail.com\nhotmail.com\noutlook.com'}
              className="font-mono text-xs"
              data-testid="pomo-blocked-domains-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Eén domein per regel (komma's en puntkomma's worden ook geaccepteerd).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Vitality Interventions */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-semibold flex items-center gap-2">
              <Wind className="w-4 h-4 text-emerald-600" /> Vitality Coach interventies
            </h3>
            <Button onClick={addIntervention} size="sm" variant="outline" data-testid="pomo-add-intervention-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Voeg interventie toe
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Deze worden tijdens de pauze getoond. De tool wisselt automatisch en de gebruiker kan ook handmatig wisselen.
          </p>

          {(config.interventions || []).map((iv, i) => (
            <div key={iv.id || i} className="border rounded-md p-3 space-y-2">
              <div className="grid sm:grid-cols-12 gap-2 items-end">
                <div className="sm:col-span-3">
                  <Label className="text-xs">Type</Label>
                  <select
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={iv.type || 'breathing'}
                    onChange={(e) => updateIntervention(i, { type: e.target.value })}
                  >
                    {INTERVENTION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-6">
                  <Label className="text-xs">Titel</Label>
                  <Input value={iv.title || ''} onChange={(e) => updateIntervention(i, { title: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Duur (sec)</Label>
                  <Input type="number" min="10" max="900" value={iv.duration_sec || ''} onChange={(e) => updateIntervention(i, { duration_sec: Number(e.target.value) || null })} />
                </div>
                <div className="sm:col-span-1 flex items-end justify-end gap-1">
                  <Button variant="ghost" size="icon" onClick={() => moveIntervention(i, -1)} disabled={i === 0}>
                    <ChevronUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => moveIntervention(i, 1)} disabled={i === (config.interventions.length - 1)}>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Instructie / body</Label>
                <Textarea rows={2} value={iv.body || ''} onChange={(e) => updateIntervention(i, { body: e.target.value })} />
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => removeIntervention(i)} className="text-red-600 hover:text-red-700">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Verwijder
                </Button>
              </div>
            </div>
          ))}

          {(config.interventions || []).length === 0 && (
            <p className="text-sm text-muted-foreground italic">Nog geen interventies — voeg er minstens 1 toe.</p>
          )}
        </CardContent>
      </Card>

      {/* Recente sessies */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-orange-600" /> Recente sessies ({sessions.length})
            </h3>
            <Button variant="ghost" size="sm" onClick={loadSessions} disabled={sessionsLoading}>
              {sessionsLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
              Refresh
            </Button>
          </div>
          {sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nog geen sessies gestart.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {sessions.map((s) => (
                <div key={s.id} className="text-sm border rounded-md p-2.5 flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{s.contact?.name || '—'} <span className="text-muted-foreground font-normal">· {s.contact?.email || '—'}</span></div>
                    <div className="text-xs text-muted-foreground">
                      {s.profile_label || s.profile_key} · {s.focus_min}m focus / {s.break_min}m pauze
                      {s.created_at && ` · ${new Date(s.created_at).toLocaleString('nl-NL')}`}
                    </div>
                  </div>
                  {s.stats && (
                    <div className="flex items-center gap-3 text-xs">
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                        {s.stats.completed_focus_blocks || 0} focus
                      </Badge>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        {s.stats.completed_breaks || 0} pauze
                      </Badge>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking CTA */}
      <BookingCtaSettings
        cfg={config}
        onChange={(field, value) => setConfig((c) => ({ ...c, [field]: value }))}
      />

      {/* Actions */}
      <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-border py-3 -mx-1 px-1 flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" onClick={handleReset} disabled={saving} data-testid="pomo-reset-btn">
          <RotateCcw className="w-4 h-4 mr-1" /> Standaard herstellen
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="pomo-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Opslaan
        </Button>
      </div>
    </div>
  );
}
